import { Info, TriangleAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { JsonBlock, JsonPostLocale } from "@/lib/blog/schema";
import { InlineText } from "./InlineText";

export const proseClass = "text-base sm:text-lg leading-relaxed text-muted-foreground";
function Block({ block }: { block: JsonBlock }) {
  switch (block.type) {
    case "p": return <p className={proseClass}><InlineText text={block.text} /></p>;
    case "h3": return <h3 className="font-heading text-xl font-semibold text-foreground"><InlineText text={block.text} /></h3>;
    case "bullets":
    case "steps": {
      const Tag = block.type === "steps" ? "ol" : "ul";
      return <Tag className={`pl-6 space-y-2.5 text-base leading-relaxed text-foreground/85 ${block.type === "steps" ? "list-decimal" : "list-disc"}`}>
        {block.items.map((item, i) => <li key={i} className="pl-1 marker:text-primary"><InlineText text={item} /></li>)}
      </Tag>;
    }
    case "figure": return <figure className="overflow-hidden rounded-2xl border border-border/60 bg-card/40">
      <img src={block.src} width={block.width} height={block.height} alt={block.alt} loading="lazy" decoding="async" className="w-full h-auto" />
      <figcaption className="p-4 text-sm leading-relaxed text-muted-foreground"><InlineText text={block.caption} /></figcaption>
    </figure>;
    case "table": return <div className="overflow-x-auto rounded-2xl border border-border/60 bg-card/40" role="region" aria-label={block.caption} tabIndex={0}>
      <table className="w-full min-w-[600px] border-collapse text-sm">
        <caption className="text-left px-4 py-3 font-medium text-muted-foreground"><InlineText text={block.caption} /></caption>
        <thead><tr>{block.headers.map((header, i) => <th key={i} scope="col" className="bg-muted/40 px-4 py-3 text-left font-semibold text-foreground"><InlineText text={header} /></th>)}</tr></thead>
        <tbody>{block.rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j} className="border-t border-border/50 px-4 py-3 text-left align-top text-foreground/85"><InlineText text={cell} /></td>)}</tr>)}</tbody>
      </table>
    </div>;
    case "callout": {
      const Icon = block.tone === "warning" ? TriangleAlert : Info;
      return <Alert className={block.tone === "warning" ? "border-accent/40 bg-accent/5" : "border-primary/30 bg-primary/5"}>
        <Icon className="h-4 w-4" aria-hidden="true" /><AlertTitle><InlineText text={block.title} /></AlertTitle>
        <AlertDescription className="text-muted-foreground leading-relaxed"><InlineText text={block.text} /></AlertDescription>
      </Alert>;
    }
  }
}
export function JsonSections({ sections }: { sections: JsonPostLocale["sections"] }) {
  return <div className="space-y-12 md:space-y-14">{sections.map((section, i) => <section key={i} className="scroll-mt-28">
    <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground mb-4">{section.heading}</h2>
    <div className="space-y-5">{section.blocks.map((block, j) => <Block key={j} block={block} />)}</div>
  </section>)}</div>;
}
